/**
 * «Править → сохранить как версию» (спека §5.4, §9.2; v2 §1.2–1.5): поля версии в полях формы,
 * сохранение — новая версия `manual` целиком (`POST scenario/manual`), старая не трогается. Форма — v2:
 * детали платья (`dress_details`), «лицо в кадре» у каждого кадра (`face_visible`); титра и звука в форме
 * нет. Версия v1 приходит уже переведённой (`toV2`): детали — из «акцентов» кадров, лицо — по крупности;
 * без этого сервер ответил бы 422.
 *
 * Проверка — серверная (`validate_scenario`, потолок кадра 15 с): 422 `BAD_SCENARIO` приходит текстом
 * причины; пустые детали платья видны до сохранения (`v2Gaps`). Инвариант, героиню в промпте
 * стилла, хвост негатива и «без лица» у финала со спины сервер допишет сам и отметит в `_fixes`.
 */

import clsx from "clsx";
import { Plus, Save, Trash2 } from "lucide-react";
import { useState, type ReactNode } from "react";

import { Btn, ErrorLine, Hint, inputClass, selectClass } from "@/components/project/ui";
import { errorText } from "@/lib/project";
import {
  FRAMING_LABEL,
  SHOT_TRANSITION_LABEL,
  useManualScenario,
  type Framing,
  type ScenarioShot,
  type ShotTransition,
} from "@/lib/video";
import {
  DETAIL_MAX,
  DETAILS_MAX,
  scenarioSumS,
  splitTarget,
  SUM_TOLERANCE_MS,
  v2Gaps,
  type ScenarioDraftV2,
} from "@/lib/videoTimeline";

import { FaceToggle, Sec } from "./bits";

const SHOTS_MIN = 2;
const SHOTS_MAX = 6;

/** Черновик версии — форма v2 (без титра, звука и `_fixes`). */
export type ScenarioDraft = ScenarioDraftV2<ScenarioShot>;
type TopKey = Exclude<keyof ScenarioDraft, "shots" | "dress_details">;

/** Поля уровня ролика: подпись, лимит схемы (§5.2, v2 §1.3), многострочное ли. */
const TOP: { key: TopKey; label: string; max: number; long?: boolean }[] = [
  { key: "logline", label: "Логлайн", max: 300, long: true },
  { key: "hook", label: "Хук (первые 2 с, без текста)", max: 200 },
  { key: "negative", label: "Негатив (EN, общий на серию)", max: 400, long: true },
  { key: "heroine", label: "Героиня (EN, дословно в каждый промпт стилла)", max: 300, long: true },
  { key: "light", label: "Свет (EN)", max: 200 },
];

const FRAMINGS = Object.keys(FRAMING_LABEL) as Framing[];
const TRANSITIONS = Object.keys(SHOT_TRANSITION_LABEL) as ShotTransition[];

type DraftShot = ScenarioDraft["shots"][number];

const emptyShot = (duration_s: number, framing: Framing, face_visible: boolean): DraftShot => ({
  title: "",
  duration_s,
  framing,
  camera: "",
  action: "",
  dress_focus: "",
  keyframe_prompt: "",
  video_prompt: "",
  transition: "cut",
  face_visible,
});

/** Пустая версия «написать руками»: три кадра по кривой medium → wide → back (финал без лица), цель поровну. */
export function blankScenario(targetMs: number): ScenarioDraft {
  const [a = 5, b = 5, c = 5] = splitTarget(targetMs, 3);
  return {
    logline: "",
    hook: "",
    negative: "",
    heroine: "",
    light: "",
    dress_details: [],
    shots: [emptyShot(a, "medium", true), emptyShot(b, "wide", true), emptyShot(c, "back", false)],
  };
}

const cleanDetails = (lines: readonly string[]): string[] => lines.map((x) => x.trim()).filter(Boolean);

export function ScenarioEditor({
  pid,
  initial,
  basedOn,
  fromV1 = false,
  targetMs,
  onCancel,
  onSaved,
}: {
  pid: number;
  initial: ScenarioDraft;
  /** Номер версии-основы; `null` — пишем с нуля. */
  basedOn: number | null;
  /** Основа — версия v1: детали и лицо переведены, их стоит проверить. */
  fromV1?: boolean;
  targetMs: number;
  onCancel: () => void;
  onSaved: (scenarioId: number) => void;
}) {
  const manual = useManualScenario(pid);
  const [draft, setDraft] = useState<ScenarioDraft>(() => ({
    ...initial,
    dress_details: [...initial.dress_details],
    shots: initial.shots.map((s) => ({ ...s })),
  }));
  const sumMs = Math.round(scenarioSumS(draft.shots) * 1000);
  const off = Math.abs(sumMs - targetMs) > SUM_TOLERANCE_MS;
  const gaps = v2Gaps(draft);

  const setTop = (key: TopKey, value: string) => setDraft((d) => ({ ...d, [key]: value }));
  const setShot = (n: number, patch: Partial<DraftShot>) =>
    setDraft((d) => ({ ...d, shots: d.shots.map((s, i) => (i === n ? { ...s, ...patch } : s)) }));

  const save = () => {
    if (manual.isPending || gaps.length) return;
    manual.mutate(
      { ...draft, dress_details: cleanDetails(draft.dress_details) },
      { onSuccess: (r) => onSaved(r.scenario.id) },
    );
  };

  return (
    <div className="grid gap-3" data-annot="правка сценария">
      <Hint>
        {basedOn !== null ? `Правка версии v${basedOn} — сохранится новой версией, v${basedOn} останется.` : "Сценарий руками — сохранится новой версией."}{" "}
        Кадров {SHOTS_MIN}–{SHOTS_MAX}, длительность кадра 2–15 с, шаг 0,5; сумма (наплыв −0,3 с) — цель ±0,5 с.
      </Hint>
      {fromV1 ? (
        <div className="rounded-[var(--radius-ui)] border border-warn/40 bg-warn-bg px-2.5 py-1.5 text-[12px] text-warn">
          Основа — версия v1: детали платья взяты из «акцентов» кадров (лучше —
          по-английски, коротко), лицо в кадре — по крупности. Проверьте перед сохранением.
        </div>
      ) : null}

      <div className="grid gap-2 md:grid-cols-2">
        {TOP.map((f) => (
          <Field key={f.key} label={f.label} count={draft[f.key].length} max={f.max} wide={f.long}>
            {f.long ? (
              <textarea
                value={draft[f.key]}
                onChange={(e) => setTop(f.key, e.target.value)}
                rows={2}
                maxLength={f.max}
                className={area}
              />
            ) : (
              <input
                value={draft[f.key]}
                onChange={(e) => setTop(f.key, e.target.value)}
                maxLength={f.max}
                className={inputClass}
              />
            )}
          </Field>
        ))}
        <Field
          label={`Детали платья (EN, по строке, 1–${DETAILS_MAX}, до ${DETAIL_MAX} знаков) — инвариант видео-промптов`}
          count={cleanDetails(draft.dress_details).length}
          max={DETAILS_MAX}
          wide
        >
          <textarea
            value={draft.dress_details.join("\n")}
            onChange={(e) => setDraft((d) => ({ ...d, dress_details: e.target.value.split("\n") }))}
            rows={3}
            placeholder={"high lace collar\nsheer long lace sleeves\nopen back with buttons"}
            className={area}
          />
        </Field>
      </div>

      <div className="flex items-baseline gap-2">
        <div className="text-[13px] font-medium text-fg">Кадры</div>
        <span className={clsx("text-[12px]", off ? "text-err" : "text-fg-faint")}>
          сумма <Sec ms={sumMs} /> при цели <Sec ms={targetMs} />
        </span>
      </div>

      {draft.shots.map((s, n) => (
        <div key={n} className="grid gap-2 rounded-[var(--radius-ui)] border border-border bg-bg p-2.5" data-annot={`кадр сценария ${n + 1}`}>
          <div className="flex flex-wrap items-center gap-2">
            <b className="tnum w-5 text-fg-muted">{n + 1}</b>
            <input
              value={s.title}
              onChange={(e) => setShot(n, { title: e.target.value })}
              placeholder="заголовок (RU)"
              maxLength={60}
              className={`${inputClass} min-w-[180px] flex-1`}
            />
            <label className="flex items-center gap-1 text-[12px] text-fg-muted">
              с
              <input
                type="number"
                step={0.5}
                min={2}
                max={15}
                value={s.duration_s}
                onChange={(e) => setShot(n, { duration_s: Number(e.target.value) })}
                className={`${inputClass} w-[70px] tnum`}
              />
            </label>
            <select
              value={s.framing}
              onChange={(e) => setShot(n, { framing: e.target.value as Framing })}
              aria-label="крупность"
              className={selectClass}
            >
              {FRAMINGS.map((f) => (
                <option key={f} value={f}>
                  {FRAMING_LABEL[f]}
                </option>
              ))}
            </select>
            <select
              value={s.transition}
              onChange={(e) => setShot(n, { transition: e.target.value as ShotTransition })}
              aria-label="переход"
              className={selectClass}
            >
              {TRANSITIONS.map((t) => (
                <option key={t} value={t}>
                  переход: {SHOT_TRANSITION_LABEL[t]}
                </option>
              ))}
            </select>
            <FaceToggle
              value={s.face_visible ? "visible" : "hidden"}
              onChange={(next) => setShot(n, { face_visible: next === "visible" })}
            />
            <Btn
              tone="ghost"
              title="убрать кадр"
              aria-label="убрать кадр"
              disabled={draft.shots.length <= SHOTS_MIN}
              onClick={() => setDraft((d) => ({ ...d, shots: d.shots.filter((_, i) => i !== n) }))}
            >
              <Trash2 size={13} />
            </Btn>
          </div>
          <div className="grid gap-2 md:grid-cols-3">
            <Field label="Камера (EN)" count={s.camera.length} max={80}>
              <input value={s.camera} onChange={(e) => setShot(n, { camera: e.target.value })} maxLength={80} className={inputClass} />
            </Field>
            <Field label="Действие (RU)" count={s.action.length} max={200}>
              <input value={s.action} onChange={(e) => setShot(n, { action: e.target.value })} maxLength={200} className={inputClass} />
            </Field>
            <Field label="Акцент платья (RU)" count={s.dress_focus.length} max={120}>
              <input
                value={s.dress_focus}
                onChange={(e) => setShot(n, { dress_focus: e.target.value })}
                maxLength={120}
                className={inputClass}
              />
            </Field>
          </div>
          <div className="grid gap-2 md:grid-cols-2">
            <Field label="Промпт стилла (EN)" count={s.keyframe_prompt.length} max={900}>
              <textarea
                value={s.keyframe_prompt}
                onChange={(e) => setShot(n, { keyframe_prompt: e.target.value })}
                rows={4}
                maxLength={900}
                className={area}
              />
            </Field>
            <Field label="Видео-промпт (EN, только движение)" count={s.video_prompt.length} max={600}>
              <textarea
                value={s.video_prompt}
                onChange={(e) => setShot(n, { video_prompt: e.target.value })}
                rows={4}
                maxLength={600}
                className={area}
              />
            </Field>
          </div>
        </div>
      ))}

      {gaps.length ? <div className="text-[12px] text-warn">Не хватает: {gaps.join("; ")}.</div> : null}
      <div className="flex flex-wrap items-center gap-2">
        <Btn
          disabled={draft.shots.length >= SHOTS_MAX}
          onClick={() => setDraft((d) => ({ ...d, shots: [...d.shots, emptyShot(5, "wide", true)] }))}
        >
          <Plus size={13} />
          Добавить кадр
        </Btn>
        <span className="flex-1" />
        <Btn tone="ghost" onClick={onCancel} disabled={manual.isPending}>
          Отмена
        </Btn>
        <Btn tone="primary" onClick={save} disabled={manual.isPending || gaps.length > 0}>
          <Save size={13} />
          {manual.isPending ? "сохраняю…" : "Сохранить как версию"}
        </Btn>
      </div>
      <ErrorLine text={manual.isError ? errorText(manual.error) : ""} />
    </div>
  );
}

const area =
  "w-full resize-y rounded-[var(--radius-ui)] border border-border bg-bg px-2.5 py-1.5 text-[12.5px] leading-[1.5] text-fg outline-none placeholder:text-fg-faint focus:border-accent";

function Field({
  label,
  count,
  max,
  wide,
  children,
}: {
  label: string;
  count: number;
  max: number;
  wide?: boolean | undefined;
  children: ReactNode;
}) {
  return (
    <label className={clsx("grid gap-0.5", wide && "md:col-span-2")}>
      <span className="flex text-[11.5px] text-fg-faint">
        {label}
        <span className={clsx("ml-auto tnum", count > max * 0.9 && "text-warn")}>
          {count}/{max}
        </span>
      </span>
      {children}
    </label>
  );
}
